import { countPotentialIncidents, countPriorityIncidents } from "./priority";
import type { WasteSenseReportPayload } from "./types";

export interface WasteSenseSummaryStats {
  /** Total camera events received and processed. */
  eventsProcessed: number;
  /** Events that completed AI review successfully. */
  aiReviewedEvents: number;
  /** AI review completion rate (0–100). */
  analysedPct: number;
  /**
   * Events flagged by AI as potentially dumping, before strict
   * customer-safe review.
   */
  potentialEvents: number;
  /**
   * Priority incidents — events retained after strict customer-safe
   * review for council follow-up.
   */
  priorityIncidents: number;
}

/**
 * Compute the four headline KPIs for the "Weekly summary" block.
 *
 * Single source of truth: rows. Whenever a site ships row-level data,
 * the KPIs are derived from those rows via the same predicates the
 * priority hero list, the per-site cards, and the map badges use —
 * so every surface in the report agrees on what counts as a priority
 * or potential incident.
 *
 * Rollup fallbacks only kick in when row-level data is missing:
 *
 *   - Events processed/reviewed: prefer `summary.events_scanned` /
 *     `summary.ai_reviewed_events`; fall back to per-site
 *     `observations`.
 *   - Potentials (`detected_true_events`): if any site ships rows,
 *     count rows where `detected === true`. Otherwise fall back to
 *     the report-level `summary.detected_true_events` rollup, then
 *     to the priority count, then to 0.
 *   - Priorities (`detected_high_conf_events` / `dd_detections`):
 *     if any site ships rows, count rows where
 *     `detected === true && confidence > 0.95`. Otherwise fall back
 *     to per-site `dd_detections` rollups via
 *     `countPrioritySiteIncidents`, then to 0.
 *
 * See `priority.ts` for why we prefer rows over rollups (short
 * version: the legacy generator preferred rollups because its row
 * table was truncated server-side; ours isn't, and disagreement
 * between the rollup and the rows is a payload bug we want to
 * surface, not paper over).
 */
export function computeSummaryStats(payload: WasteSenseReportPayload): WasteSenseSummaryStats {
  const totalObservations = payload.sites.reduce((acc, s) => acc + (s.observations ?? s.events ?? 0), 0);

  const anySiteHasRows = payload.sites.some((s) => Array.isArray(s.rows));

  // Priority counts derive per-site via `countPrioritySiteIncidents`,
  // which now prefers rows and falls back to `dd_detections` only when
  // rows are absent. Same predicate every renderer uses, so the KPI
  // agrees with the hero list and the per-site map list down to the row.
  const priorityIncidents = countPriorityIncidents(payload.sites);

  // Potentials follow the same shape: rows when present, fall through
  // to the report-level rollup only when no site shipped rows so older
  // summary-only payloads still display the right number.
  const potentialEvents =
    anySiteHasRows ?
      countPotentialIncidents(payload.sites)
    : (payload.summary?.detected_true_events ?? priorityIncidents);

  const eventsProcessed = payload.summary?.events_scanned ?? totalObservations;
  const aiReviewedEvents = payload.summary?.ai_reviewed_events ?? totalObservations;

  const analysedPct = eventsProcessed > 0 ? (aiReviewedEvents / eventsProcessed) * 100 : 0;

  return {
    eventsProcessed,
    aiReviewedEvents,
    analysedPct,
    potentialEvents,
    priorityIncidents,
  };
}

/**
 * Format the reporting window like the static report:
 * "25 May 2026 to 31 May 2026 AEST (7 days)".
 *
 * Times come back from the pipeline in UTC; we render them in the
 * device's local timezone for now (close enough until we wire in
 * tenant-specific timezones).
 */
export function formatReportingWindow(payload: WasteSenseReportPayload): string {
  const start = new Date(payload.window_start_utc);
  // The end is inclusive in display terms — the source ends at the last
  // second of the day, so we don't need any subtraction here.
  const end = new Date(payload.window_end_utc);

  const dateFmt = new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const tzFmt = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" });
  const tzPart = tzFmt.formatToParts(end).find((p) => p.type === "timeZoneName")?.value ?? "";

  return `${dateFmt.format(start)} to ${dateFmt.format(end)}${tzPart ? ` ${tzPart}` : ""} (${payload.window_days} days)`;
}
